import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { MessageSquare, Send, Loader2 } from "lucide-react";

export default function BookingChat({ bookingId, open, onOpenChange, label }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/bookings/${bookingId}/messages`);
      setMessages(data);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (!open || !bookingId) return;
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [open, bookingId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/bookings/${bookingId}/messages`, { text });
      setMessages((m) => [...m, data]);
      setInput("");
    } finally { setBusy(false); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="bg-[#0B0D12] border-slate-800 w-full sm:max-w-md flex flex-col p-0" data-testid="booking-chat-drawer">
        <SheetHeader className="px-6 py-5 border-b border-slate-800">
          <SheetTitle className="flex items-center gap-2 text-white font-display">
            <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center">
              <MessageSquare className="w-4 h-4 text-slate-950" />
            </div>
            Trip chat
          </SheetTitle>
          {label && <p className="text-xs text-slate-400 font-mono truncate">{label}</p>}
        </SheetHeader>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-4 space-y-3" data-testid="messages-list">
          {messages.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-8">No messages yet. Say hi 👋</p>
          ) : messages.map((m) => {
            const mine = m.author_id === user?.id;
            return (
              <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`message-${m.message_id}`}>
                <div className={`max-w-[80%] px-4 py-2 rounded-2xl ${mine ? "bg-blue-500 text-white rounded-tr-sm" : "bg-slate-800 text-slate-100 rounded-tl-sm"}`}>
                  {!mine && <p className="text-[10px] font-mono uppercase tracking-widest text-cyan-300 mb-0.5">{m.author_name} · {m.author_role}</p>}
                  <p className="text-sm whitespace-pre-wrap leading-relaxed">{m.text}</p>
                  <p className="text-[10px] opacity-60 mt-1">{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</p>
                </div>
              </div>
            );
          })}
        </div>

        <div className="p-4 border-t border-slate-800">
          <div className="flex gap-2">
            <Input value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Type a message…" data-testid="chat-input"
              className="bg-slate-900 border-slate-800 focus-visible:ring-blue-500" />
            <Button onClick={send} disabled={busy || !input.trim()} data-testid="chat-send-btn"
              className="bg-blue-500 hover:bg-blue-600 shrink-0">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
